/**
 * Tiny inline area chart using Recharts.
 */

import { useId } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";

type SparkChartProps = {
  data: { value: number }[];
  color?: string;
  height?: number;
  className?: string;
};

export function SparkChart({
  // Command Deck signal accent. Was `hsl(var(--primary))` — both the
  // var name (`--color-primary`, not `--primary`) and the hsl() wrapper
  // (token is a hex) were wrong, so the stroke never rendered. Now a
  // valid token reference; callers can still override per-metric.
  data,
  color = "var(--color-primary)",
  height = 40,
  className,
}: SparkChartProps) {
  // Gradient ids must be stable + valid (the old `spark-${color}` baked
  // `var(...)`/parens into an SVG id → invalid url() ref).
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9-]/g, "")}`;
  if (data.length === 0) return null;

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
